import { logger } from '../../../utils/logger.js';
import { safeJsonParse } from '../../../utils/safeJsonParse.js';

class CtlStepper extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._currentStep = 0;
    this._steps = [];
    this._slotListenerAdded = false;
    this._slotChangeHandler = null;
  }

  static get observedAttributes() {
    return ['current-step', 'orientation', 'steps'];
  }

  connectedCallback() {
    // Render first to create the slot
    this.render();
    
    // Then parse steps after slot exists
    this._parseSteps();
    
    // Re-render with parsed steps
    this.render();
    
    // Listen for changes to slotted step elements
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot && !this._slotListenerAdded) {
      this._slotListenerAdded = true;
      this._slotChangeHandler = () => {
        this._parseSteps();
        this.render();
      };
      slot.addEventListener('slotchange', this._slotChangeHandler);
    }
  }

  disconnectedCallback() {
    // Clean up event listener to prevent memory leak
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot && this._slotChangeHandler) {
      slot.removeEventListener('slotchange', this._slotChangeHandler);
      this._slotChangeHandler = null;
      this._slotListenerAdded = false;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'current-step') {
      this._currentStep = parseInt(newValue) || 0;
      this.render();
    } else if (name === 'orientation') {
      this.render();
    } else if (name === 'steps') {
      this._parseSteps();
      this.render();
    }
  }

  _parseSteps() {
    // Parse steps from data-steps attribute or child elements
    const stepsAttr = this.getAttribute('steps');
    if (stepsAttr) {
      this._steps = safeJsonParse(stepsAttr, []);
      if (this._steps.length > 0) {
        return; // If JSON steps provided, use them
      }
    }
    
    // Or get from slot content
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot) {
      const assigned = slot.assignedElements();
      if (assigned.length > 0) {
        this._steps = assigned.map((el, index) => ({
          label: el.getAttribute('label') || `Step ${index + 1}`,
          description: el.getAttribute('description') || '',
          status: el.getAttribute('status') || 'pending'
        }));
      }
    }
  }

  get currentStep() {
    return this._currentStep;
  }

  set currentStep(value) {
    this._currentStep = value;
    this.setAttribute('current-step', value);
  }

  nextStep() {
    if (this._currentStep < this._steps.length - 1) {
      this.currentStep = this._currentStep + 1;
      this._dispatchStepChange('next');
    }
  }

  previousStep() {
    if (this._currentStep > 0) {
      this.currentStep = this._currentStep - 1;
      this._dispatchStepChange('previous');
    }
  }

  goToStep(index) {
    if (index >= 0 && index < this._steps.length) {
      this.currentStep = index;
      this._dispatchStepChange('goto');
    }
  }

  _dispatchStepChange(action) {
    this.dispatchEvent(new CustomEvent('ctl-step-change', {
      bubbles: true,
      composed: true,
      detail: {
        currentStep: this._currentStep,
        totalSteps: this._steps.length,
        action: action
      }
    }));
  }

  render() {
    const orientation = this.getAttribute('orientation') || 'horizontal';
    const currentStep = this._currentStep;
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--font-family-base, 'Segoe UI', sans-serif);
        }

        .stepper {
          display: flex;
          flex-direction: ${orientation === 'vertical' ? 'column' : 'row'};
          ${orientation === 'horizontal' ? 'align-items: center;' : ''}
          gap: ${orientation === 'vertical' ? 'var(--ctl-space-4, 16px)' : '0'};
        }

        .step {
          display: flex;
          align-items: center;
          flex: ${orientation === 'horizontal' ? '1' : 'none'};
          position: relative;
        }

        .step-indicator {
          display: flex;
          align-items: center;
          gap: var(--ctl-space-3, 12px);
        }

        .step-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--font-weight-bold, 700);
          font-size: var(--font-size-md, 1rem);
          transition: all var(--transition-normal, 0.2s ease);
          flex-shrink: 0;
        }

        .step.pending .step-circle {
          background: var(--ctl-color-surface, #f5f5f5);
          border: 2px solid var(--ctl-color-border, #ccc);
          color: var(--ctl-color-muted, #666);
        }

        .step.active .step-circle {
          background: var(--ctl-color-brand, #6200ee);
          border: 2px solid var(--ctl-color-brand, #6200ee);
          color: white;
          box-shadow: 0 0 0 4px color-mix(in srgb, var(--ctl-color-brand, #6200ee) 20%, transparent);
        }

        .step.complete .step-circle {
          background: var(--ctl-color-success, #218838);
          border: 2px solid var(--ctl-color-success, #218838);
          color: white;
        }

        .step-content {
          display: flex;
          flex-direction: column;
          gap: var(--ctl-space-1, 4px);
        }

        .step-label {
          font-weight: var(--font-weight-semibold, 600);
          font-size: var(--font-size-md, 1rem);
          color: var(--ctl-color-text, #000);
        }

        .step.pending .step-label {
          color: var(--ctl-color-muted, #666);
        }

        .step-description {
          font-size: var(--font-size-sm, 0.875rem);
          color: var(--ctl-color-muted, #666);
        }

        .step-connector {
          flex: 1;
          height: 2px;
          background: var(--ctl-color-border, #ccc);
          margin: 0 var(--ctl-space-3, 12px);
          transition: background var(--transition-normal, 0.2s ease);
        }

        .step.complete + .step .step-connector,
        .step.complete .step-connector {
          background: var(--ctl-color-success, #218838);
        }

        .step-connector.vertical {
          width: 2px;
          height: 40px;
          margin: 0;
          margin-left: 19px;
        }

        /* Hide last connector in horizontal mode */
        .step:last-child .step-connector {
          display: ${orientation === 'horizontal' ? 'none' : 'block'};
        }

        .checkmark {
          width: 20px;
          height: 20px;
        }
      </style>

      <div class="stepper">
        ${this._steps.map((step, index) => {
          const status = index < currentStep ? 'complete' : index === currentStep ? 'active' : 'pending';
          return `
            <div class="step ${status}" data-step="${index}">
              <div class="step-indicator">
                <div class="step-circle">
                  ${status === 'complete' ? `
                    <svg class="checkmark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ` : index + 1}
                </div>
                <div class="step-content">
                  <div class="step-label">${step.label}</div>
                  ${step.description ? `<div class="step-description">${step.description}</div>` : ''}
                </div>
              </div>
              ${orientation === 'horizontal' ? '<div class="step-connector"></div>' : ''}
            </div>
            ${orientation === 'vertical' && index < this._steps.length - 1 ? '<div class="step-connector vertical"></div>' : ''}
          `;
        }).join('')}
      </div>

      <div style="display: none;">
        <slot></slot>
      </div>
    `;

    // Add click listeners to steps
    const stepElements = this.shadowRoot.querySelectorAll('.step');
    stepElements.forEach((stepEl, index) => {
      stepEl.style.cursor = 'pointer';
      stepEl.addEventListener('click', () => {
        this.goToStep(index);
      });
    });
  }
}

customElements.define("ctl-stepper", CtlStepper);

export { CtlStepper };
